import { registerDecorators as _registerDecorators, registerComponent as _registerComponent, LightningElement } from "lwc";
import _tmpl from "./listingDetail.html";
import { selectParams, WireStore } from '@oneappexchange/lwc-wired';
import { schemas } from '@oneappexchange/oaf-schema';
import * as kagekiri from 'kagekiri';
import equalityFn from 'zustand/shallow';
import { FORCE_COPILOT_LISTING_TYPE } from '~/constants';
import { getChannel } from '~/modules/analytics/channel';
import { FeatureFlags } from '~/modules/feature_flags';
import { generateSimpleBadge } from '~/modules/helpers/badgeHelper';
import { selectCompatibleWith, selectRequiredProducts, selectRequiredListings } from '~/modules/helpers/compatibilityHelper';
import { getListingLogo } from '~/modules/helpers/listingHelper';
import { generateBaseUrl, generateLegacyListingUrl } from '~/modules/helpers/routingHelper';
import { Routes } from '~/modules/routes';
import { initialPageRenderedMetricsCheck } from '~/modules/services/metricsService';
import { i18nStore, selectI18n } from '~/modules/stores/i18nStore';
import { listingStore, selectListing } from '~/modules/stores/listingStore';
import { recentItemStore } from '~/modules/stores/recentItemStore';
import { navigate, routerStore } from '~/modules/stores/routerStore';
import { SavedListingsLoadingStatus, selectUserFavorite, userStore } from '~/modules/stores/userStore';
import { DeviceType, selectDeviceType, viewportStore } from '~/modules/stores/viewportStore';
import { createCookie, getCookie } from '~/modules/utilities/cookies';
import { sanitizeText } from '~/modules/utilities/html_utilities';
import { forceAPIListingExtensions, forcePackageListingExtensions, generateListingUrl, hasAgenticDetails, isListingMatchingId, isSupportedTechnologyType, selectExtensionListing, selectListingTryOptions } from '~/modules/utilities/listing_utils';
import { checkReferralParams, checkUtmParams, getUtmParamsFromLocalStorage, isExpired, REFERRAL_CODE_PARAM, storeAnalyticsReferrer, updateLocalStorage } from '~/modules/utilities/localStorageUtilities';
import { logger } from '~/modules/utilities/logger';
import { sendNotification } from '~/modules/utilities/notificationUtilities';
import { selectLeadTrialInformation } from '~/modules/utilities/oaf_schema_utils';
import { upsertQueryParams } from '~/modules/utilities/router';
import { parseNewLines } from '~/modules/utilities/text';
const TRIAL_INFORMATION_COOKIE = 'TRIAL_INFORMATION_09-04-2024';
const OVERVIEW_TAB_CODE = 'e';
const REVIEWS_TAB_CODE = 'r';
const MORE_DETAILS_TAB_CODE = 'd';
const CTA_PARAM = 'cta';
const EXTENSION_TYPE_PACKAGE_LISTING = 'listing/extensions/force/listings/Listing/extensions/PackageListing';
const EXTENSION_TYPE_PACKAGE_TEST_DRIVE = 'listing/extensions/force/listings/Listing/extensions/PackageListing/TestDrive';
const EXTENSION_TYPE_API_LISTING = 'listing/extensions/force/listings/Listing/extensions/APIListing';
const EXTENSION_TYPE_API_TEST_DRIVE = 'listing/extensions/force/listings/Listing/extensions/APIListing/TestDrive';
class ListingDetail extends LightningElement {
  constructor(...args) {
    super(...args);
    // @ts-expect-error * Automated Type Error Fix
    this.i18n = null;
    // @ts-expect-error * Automated Type Error Fix
    this.i18nModule = null;
    this.userAuthenticated = false;
    this.isLoading = false;
    this.userToken = '';
    this.userId = '';
    this.savedListings = [];
    this.savedListingsLoadingStatus = SavedListingsLoadingStatus.UNINITILIAZED;
    this.isListingLoading = true;
    // @ts-expect-error * Automated Type Error Fix
    this.listing = null;
    // @ts-expect-error * Automated Type Error Fix
    this.publisher = null;
    // @ts-expect-error * Automated Type Error Fix
    this.mobileMediaQuery = null;
    // holding the listing ID so we can easily respond to
    // store updates; this is tied to router params arg
    this.currentListingId = '';
    this.prerequisiteListing = null;
    this.showOverview = true;
    this.showReviews = false;
    this.showMoreDetails = false;
    this.tabsIds = null;
    this.setTabsAriaControls = null;
    this.focusActiveTab = null;
    this.currentTab = null;
    this.isQualifiedChatInitialized = false;
    this.showQualifiedChatPanel = false;
    this.isQualifiedChatLoading = false;
    this.tabs = [{
      id: 'overview-tab',
      code: OVERVIEW_TAB_CODE,
      label: i18nStore.getState().i18nStaticCache.listingDetails.tabs.overview,
      active: true,
      order: 1,
      url: `/${Routes.LISTING}?${upsertQueryParams(['tab', OVERVIEW_TAB_CODE])}`
    }, {
      id: 'reviews-tab',
      code: REVIEWS_TAB_CODE,
      label: i18nStore.getState().i18nStaticCache.listingDetails.tabs.reviews,
      active: false,
      order: 2,
      url: `/${Routes.LISTING}?${upsertQueryParams(['tab', REVIEWS_TAB_CODE])}`
    }, {
      id: 'moreDetails-tab',
      code: MORE_DETAILS_TAB_CODE,
      label: i18nStore.getState().i18nStaticCache.listingDetails.tabs.moreDetails,
      active: false,
      order: 3,
      url: `/${Routes.LISTING}?${upsertQueryParams(['tab', MORE_DETAILS_TAB_CODE])}`
    }];
    this.deviceType = void 0;
    this.showTrialInformationAlert = false;
    this.formatCarouselItem = carouselItem => {
      return {
        pluginType: 'carousel',
        ...carouselItem
      };
    };
    this.formatDemoItem = item => {
      const legacyUrl = this.generateCtaUrlLegacySite('demo');

      // This URL will open in a new tab
      let url;
      if (this.hasDemoLeadsEnabled) {
        url = legacyUrl;
      } else {
        url = item.url || legacyUrl;
      }
      return {
        pluginType: 'demo',
        url
      };
    };
  }
  async fetchPrerequisiteListings() {
    const requiredListingIds = selectRequiredListings(this.extensionListing);
    if (!requiredListingIds || requiredListingIds.length === 0) {
      this.prerequisiteListing = null;
      return;
    }
    const listingId = requiredListingIds[0];
    try {
      // Check if already in store
      let listing = selectListing(listingStore.getState(), listingId);
      if (!listing) {
        // Fetch from API if not in store
        await listingStore.fetchListing(listingId);
        listing = selectListing(listingStore.getState(), listingId);
      }
      if (listing) {
        this.prerequisiteListing = listing;
      }
    } catch (error) {
      console.error(`Failed to fetch prerequisite listing ${listingId}:`, error);
    }
  }
  routerStoreWire(params) {
    if (!params?.listingId) {
      return;
    }
    if (params.listingId !== this.currentListingId) {
      this.currentListingId = params.listingId;
      this.setOrFetchListing(params.listingId);
    }
    if (params.tab) {
      // @ts-expect-error * Automated Type Error Fix
      this.currentTab = params.tab;
    } else {
      // @ts-expect-error * Automated Type Error Fix
      this.currentTab = OVERVIEW_TAB_CODE;
    }
    for (const tab of this.tabs) {
      tab.active = tab.code === this.currentTab;
      // The URL of the tabs depends on the router store state, we must update it when the routerStore changes
      tab.url = `/${Routes.LISTING}?${upsertQueryParams(['tab', tab.code])}`;
    }
    this.showActiveTabPanel(this.currentTab);
    storeAnalyticsReferrer();
    this.handleLeadSourceTracking(params);
  }
  renderTrialInformationAlert() {
    const cookieExists = !!getCookie(TRIAL_INFORMATION_COOKIE);
    this.showTrialInformationAlert = !cookieExists && this.hasTrialOptions;
  }
  closeTrialInformationAlert() {
    this.showTrialInformationAlert = false;
    createCookie(TRIAL_INFORMATION_COOKIE, 'true');
  }
  get trialInformationAlertProps() {
    return {
      message: this.i18n.try.informationalAlert,
      actionMessage: this.sharedLabels.learnMore,
      action: this.i18n.try.informationLink
    };
  }
  get disableAutoLoadQualifiedChat() {
    return FeatureFlags.FF_DISABLE_AUTO_LOAD_QUALIFIED_CHAT;
  }
  handleLeadSourceTracking(params) {
    // We are checking UTM parameters or referral parameters to then save it and if the user decides to initiate the GIN flow when we generate the lead we can also send the user source info
    // Check UTM parameters or referral parameters and update local storage
    if (checkUtmParams(params) || checkReferralParams(params)) {
      // Replace params.listingId with external_id so when we save the UTM params or referral params we save it with the external_id
      params.listingId = this.getListingId();
      updateLocalStorage(params);
    }
  }
  async setOrFetchListing(listingId = '') {
    try {
      this.isListingLoading = true;
      // @ts-expect-error * Automated Type Error Fix
      this.listing = selectListing(listingStore.getState(), listingId);
      if (!this.listing) {
        await listingStore.fetchListing(listingId);
        // @ts-expect-error * Automated Type Error Fix
        this.listing = selectListing(listingStore.getState(), listingId);
        if (!this.listing) {
          throw new Error();
        }
      }

      // If the new listing detail doesn't support this listing type, redirect to legacy site
      if (!isSupportedTechnologyType(this.listing)) {
        const url = generateLegacyListingUrl(this.listing);
        logger.warn(`Attempt to access unsupported listing with ID '${this.currentListingId}' and Technology '${this.listing.technology}', redirecting to legacy page: ${url}`);
        window.location.assign(url);
        return;
      }
      if (this.listing.public) {
        this.publisher = this.listing.publisher;
        recentItemStore.updateRecentItems('test-user-id', {
          listingId: this.listing.oafId ?? this.listing.external_id ?? '',
          timestamp: Date.now()
        });
      }
      this.isListingLoading = false;

      // Fetch prerequisite listings after main listing is loaded
      this.fetchPrerequisiteListings();
    } catch (error) {
      const url = generateLegacyListingUrl(this.listing);
      logger.error(`Failed to find listing with ID '${listingId}' in OAF, redirecting to legacy page: ${url}`);
      window.location.assign(url);
    }
  }
  userStoreWire(userState) {
    if (userState) {
      this.userToken = userState.userToken;
      this.userId = userState.userId || '';
      // @ts-expect-error * Automated Type Error Fix
      this.savedListings = userState.savedListings;
      this.userAuthenticated = !userState.isGuestUser;
      this.isLoading = userState.isLoading;
      this.savedListingsLoadingStatus = userState.savedListingsLoadingStatus;
    }
  }
  i18nStoreWire({
    i18n,
    i18nStaticCache
  }) {
    this.i18n = i18nStaticCache;
    this.i18nModule = i18n;
    this.sharedLabels = i18nStaticCache.shared;
    const tabsLabels = i18nStaticCache.listingDetails.tabs;
    this.tabs.find(({
      code
    }) => code === OVERVIEW_TAB_CODE).label = tabsLabels.overview;
    this.tabs.find(({
      code
    }) => code === REVIEWS_TAB_CODE).label = tabsLabels.reviews;
    this.tabs.find(({
      code
    }) => code === MORE_DETAILS_TAB_CODE).label = tabsLabels.moreDetails;
  }
  get hasTrialOptions() {
    return selectListingTryOptions(this.listing).length > 0;
  }
  get tryButtonHref() {
    return `/${Routes.TRIALS}?listingId=${this.getListingId()}`;
  }

  // ELEMENT REFERENCES
  get pricingPlanRef() {
    return kagekiri.querySelector('#overview-panel listing-pricing');
  }
  get isMobilePhone() {
    return this.deviceType === DeviceType.PHONE;
  }
  get ratingSummaryLabelPosition() {
    return this.isMobilePhone ? 'bottom' : 'right';
  }
  get activeLinkRef() {
    // @ts-expect-error * Automated Type Error Fix
    return kagekiri.querySelector('i18n-link:focus', this.template.activeElement);
  }
  get activeTabGroupRef() {
    return kagekiri.querySelector('[data-id="tab-group"] a[aria-selected="true"]');
  }
  get logoAlt() {
    return this.listing.name ? this.i18nModule.shared.logoAlt({
      LOGO_NAME: this.listing.name
    }) : this.sharedLabels.deafultLogoAlt;
  }
  connectedCallback() {
    this.renderTrialInformationAlert();
  }
  renderedCallback() {
    /* Set accessibility attributes */
    if (this.tabsIds) {
      this.setTabAttributes();
    }
    const {
      params
    } = routerStore.getState();
    if (this.currentTab && this.focusActiveTab && params?.tab) {
      // @ts-expect-error * Automated Type Error Fix
      this.focusActiveTab(this.currentTab);
      this.currentTab = null;
    }
    initialPageRenderedMetricsCheck();
    // for Agentforce button //
    if (this.displayChat && !FeatureFlags.FF_DISABLE_AUTO_LOAD_QUALIFIED_CHAT) {
      const chatButton = document.body.querySelector('#agentforceChatButton');
      if (chatButton) {
        chatButton.style.right = '5rem';
      }
    }
  }

  /**
   * Call to Actions methods included here: Get it Now & Watch a demo
   */
  handleGetItNowCTA() {
    this.redirectCallToActionToLegacySite('gin');
  }
  handleOpenChat() {
    if (!this.isQualifiedChatInitialized) {
      this.isQualifiedChatInitialized = true;
    }
    if (!this.showQualifiedChatPanel) {
      this.isQualifiedChatLoading = true;
    }
    this.showQualifiedChatPanel = true;
  }
  handleHideQualifiedChat() {
    this.showQualifiedChatPanel = false;
  }
  handleQualifiedChatLoaded(event) {
    this.isQualifiedChatLoading = event.detail.loading;
  }
  handlePricingSummaryLinkClick() {
    this.pricingPlanRef.focus();
  }
  get listingInUserFavorites() {
    return this.userId != null && this.listing &&
    // The listingId that we store in the userAccount comes directly from the url.
    // As a result we dont know which id it corresponds to, so we must check all the Ids.
    this.savedListings?.some(listingId => isListingMatchingId(this.listing, listingId));
  }
  async handleFavoriteListings(event) {
    event.preventDefault();
    if (!this.userAuthenticated) {
      window.SFIDWidget.login();
      return;
    }
    const action = this.listingInUserFavorites ? userStore.removeFavorite : userStore.addFavorite;
    const response = await action(this.currentListingId);
    const previousFocusedElement = this.favoriteButtonRef;
    if (response.isSuccess) {
      const {
        successMessage
      } = response;
      // @ts-expect-error * Automated Type Error Fix
      sendNotification(successMessage, {
        previousFocusedElement
      });
    } else {
      const {
        errorMessage,
        exceptionInfo
      } = response;
      // @ts-expect-error * Automated Type Error Fix
      sendNotification(errorMessage, {
        exceptionInfo,
        previousFocusedElement
      });
    }
  }
  get favoriteButtonRef() {
    const iconIteractive = this.template.querySelector('ax-icon-interactive');
    return kagekiri.querySelector('ax-button', iconIteractive);
  }
  redirectCallToActionToLegacySite(cta) {
    const url = this.generateCtaUrlLegacySite(cta);
    window.location.href = url;
  }
  generateCtaUrlLegacySite(cta) {
    const payloadString = localStorage.getItem(this.getListingId());
    const payload = payloadString ? JSON.parse(payloadString) : null;
    if (payload) {
      const utmData = payload.UTM;
      const sourceData = payload.source;
      const utmExpired = utmData && isExpired(utmData.expirationDate);
      const sourceExpired = sourceData && isExpired(sourceData.expirationDate);
      if (utmData && !utmExpired && sourceData && !sourceExpired) {
        const utmParams = getUtmParamsFromLocalStorage(this.getListingId());
        return this.generateCtaUrlWithUtmAndReferralCode(cta, utmParams, sourceData.referral_code);
      }
      if (utmData && !utmExpired) {
        const utmParams = getUtmParamsFromLocalStorage(this.getListingId());
        return this.generateCtaUrlWithUtm(cta, utmParams);
      }

      // Check if referral_code is present and not expired
      if (sourceData && !sourceExpired) {
        return this.generateLegacySiteUrlWithReferralCode(cta, sourceData.referral_code);
      }
    }

    // If both UTM and referral_code are expired or not present, generate URL with channel
    return this.generateCtaUrl(cta);
  }
  getListingId() {
    return this.listing.external_id ?? (this.listing.appExchangeId || '');
  }
  sanitizeUtmParams(utmParams) {
    if (!utmParams) {
      return null;
    }
    return Object.fromEntries(Object.entries(utmParams).map(([key, value]) => [sanitizeText(key), sanitizeText(value)]));
  }
  generateUrlWithParams(queryParams) {
    const baseUrl = generateBaseUrl();
    const siteUrl = generateListingUrl(baseUrl, this.listing, upsertQueryParams(queryParams));
    return siteUrl;
  }
  generateCtaUrlWithUtm(cta, utmParams) {
    const sanitizedUtmParams = this.sanitizeUtmParams(utmParams);
    const queryParams = {
      insert: [[CTA_PARAM, cta], ...(sanitizedUtmParams ? Object.entries(sanitizedUtmParams) : [])],
      remove: ['referral', 'ref']
    };
    return this.generateUrlWithParams(queryParams);
  }
  generateLegacySiteUrlWithReferralCode(cta, referral_code) {
    const queryParams = {
      insert: [[CTA_PARAM, cta], [REFERRAL_CODE_PARAM, referral_code]],
      remove: ['referral', 'ref']
    };
    return this.generateUrlWithParams(queryParams);
  }
  generateCtaUrlWithUtmAndReferralCode(cta, utmParams, referral_code) {
    const sanitizedUtmParams = this.sanitizeUtmParams(utmParams);
    const queryParams = {
      insert: [[CTA_PARAM, cta], [REFERRAL_CODE_PARAM, referral_code], ...(sanitizedUtmParams ? Object.entries(sanitizedUtmParams) : [])],
      remove: ['referral', 'ref']
    };
    return this.generateUrlWithParams(queryParams);
  }
  generateCtaUrl(cta) {
    const currentUrl = sanitizeText(window.location.href);
    const documentReferrer = sanitizeText(document.referrer);
    const otherSourceInfo = getChannel(currentUrl, documentReferrer, false, false, false);
    const queryParams = {
      insert: [[CTA_PARAM, cta], ['other_source', otherSourceInfo.channel]]
    };
    return this.generateUrlWithParams(queryParams);
  }

  // @ts-expect-error * Automated Type Error Fix
  handleActionsAnalytics(event) {
    const buttontName = event.detail.button?.callToAction?.name;
    let clickUrl;
    if (buttontName === 'get-it-now-btn') {
      clickUrl = this.generateCtaUrlLegacySite('gin');
      event.detail.clickUrl = clickUrl;
    }
    clickUrl = '';
  }

  // @ts-expect-error * Automated Type Error Fix
  handleTabGroupKeyDown(event) {
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      this.activeTabGroupRef.focus();
    }
  }
  /** End methods for CTA */

  /* This sets the aria-labelledby on the panels and the aria-controls con each anchor tab */
  setTabAttributes() {
    // @ts-expect-error * Automated Type Error Fix
    const [overviewTabId, reviewsTabId, moreDetailsTabId] = this.tabsIds;
    const listingOverviewTemplate = this.template.querySelector('listing-overview');
    const listingReviewsTemplate = this.template.querySelector('listing-reviews');
    const listingMoreDetailsTemplate = this.template.querySelector('listing-more-details');
    if (listingOverviewTemplate) {
      listingOverviewTemplate.setAttribute('aria-labelledby', overviewTabId);
      // @ts-expect-error * Automated Type Error Fix
      this.setTabsAriaControls(overviewTabId, listingOverviewTemplate.getAttribute('id'));
    }
    if (listingReviewsTemplate) {
      listingReviewsTemplate.setAttribute('aria-labelledby', reviewsTabId);
      // @ts-expect-error * Automated Type Error Fix
      this.setTabsAriaControls(reviewsTabId, listingReviewsTemplate.getAttribute('id'));
    }
    if (listingMoreDetailsTemplate) {
      listingMoreDetailsTemplate.setAttribute('aria-labelledby', moreDetailsTabId);
      // @ts-expect-error * Automated Type Error Fix
      this.setTabsAriaControls(moreDetailsTabId, listingMoreDetailsTemplate.getAttribute('id'));
    }
  }

  /* Registration of the necessary callbacks for accessibility and focus of tabs */
  // @ts-expect-error * Automated Type Error Fix
  handleTabRegister(event) {
    this.tabsIds = event.detail.tabsIds;
    this.setTabsAriaControls = event.detail.setAriaControls;
    this.focusActiveTab = event.detail.focusActiveTab;
  }

  // @ts-expect-error * Automated Type Error Fix
  showActiveTabPanel(tabCode) {
    this.showOverview = false;
    this.showReviews = false;
    this.showMoreDetails = false;
    switch (tabCode) {
      case OVERVIEW_TAB_CODE:
        this.showOverview = true;
        break;
      case REVIEWS_TAB_CODE:
        this.showReviews = true;
        break;
      case MORE_DETAILS_TAB_CODE:
        this.showMoreDetails = true;
        break;
      default:
        this.showOverview = true;
    }
  }
  get logo() {
    return getListingLogo(this.listing);
  }
  get averageRating() {
    return this.listing?.reviewsSummary?.averageRating || 0;
  }
  get averageRatingLabel() {
    return this.i18nModule.heading.reviewsRating({
      VALUE: this.averageRating
    });
  }
  get reviewsCount() {
    return this.listing?.reviewsSummary?.reviewCount || 0;
  }
  get reviewsCountLabel() {
    return this.i18nModule.heading.reviewsCount({
      COUNT: this.reviewsCount,
      URL: `${Routes.LISTING}?${upsertQueryParams(['tab', 'r'], ['scrollTo', 'reviews'])}`
    });
  }
  get moreDetailsLink() {
    return this.i18nModule?.listingDetails.moreDetails({
      URL: `${Routes.LISTING}?${upsertQueryParams(['tab', 'd'], ['scrollTo', 'additional-details'])}`
    });
  }
  navigateReviewsURL() {
    navigate(`${Routes.LISTING}?${upsertQueryParams(['tab', 'r'], ['scrollTo', 'reviews'])}`);
  }
  navigateMoreDetailsURL() {
    navigate(`${Routes.LISTING}?${upsertQueryParams(['tab', 'd'], ['scrollTo', 'additional-details'])}`);
    this.activeTabGroupRef.focus();
  }
  get extensionListing() {
    return selectExtensionListing(this.listing);
  }
  get pricingPlan() {
    // @ts-expect-error * Automated Type Error Fix
    return this.listing?.pricing;
  }
  get isSupportedTechnologyType() {
    return isSupportedTechnologyType(this.listing);
  }
  get solutionType() {
    if (this.extensionListing?.appType === 'Package/Platform/Data') {
      return this.i18n.search.filters.solutionType.lightningData;
    }
    return this.i18n.search.filters.solutionType[this.listing.technology];
  }
  get iconTechnologySymbol() {
    switch (this.listing.technology) {
      case 'force/package':
      case 'force/bolt':
      case 'force/flow':
      case 'force/lightning':
      case 'salesforce-platform/api':
        return 'salesforce';
      case 'b2c-commerce/package':
      case 'b2c-commerce/api':
        return 'b2c';
      case 'tableau/accelerator':
        return 'tableau';
      case 'quip/package':
        return 'quip';
      case 'marketing-cloud/api':
        return 'marketing-cloud';
      case FORCE_COPILOT_LISTING_TYPE:
        return 'copilot';
      default:
        return 'salesforce';
    }
  }
  get publisherByLine() {
    return this.listing && this.publisher ? this.i18nModule.listingDetails.publisher.byLine({
      NAME: this.publisher.name
    }) : '';
  }
  get hasAgenticDetails() {
    return hasAgenticDetails(this.listing);
  }
  get industryBadges() {
    const labels = this.i18n.oaf.supportedIndustries;
    const supportedIndustries = this.extensionListing?.supportedIndustries;
    if (supportedIndustries && supportedIndustries.length !== 0) {
      return supportedIndustries.map(key => generateSimpleBadge(labels[key] || key));
    }
    return this.extensionListing?.industries?.map(key => generateSimpleBadge(labels[key] || key)) ?? [];
  }
  get categoryBadges() {
    if (!this.listing?.businessNeeds) {
      return [];
    }
    const labels = this.i18n.oaf.category;
    const l1Categories = Object.keys(schemas['listing/ListingCategories'].get().properties);
    // @ts-expect-error * Automated Type Error Fix
    const selectedL1Categories = l1Categories.filter(l1Cat => this.listing.businessNeeds[l1Cat]?.isSelected);
    const badges = [
    // Level 1 (L1) categories
    ...(selectedL1Categories.map(l1Category => generateSimpleBadge(labels[l1Category] || l1Category)) ?? []),
    // Level 2 (L2) sub-categories
    ...l1Categories.reduce((accumulator, l1Category) => {
      // @ts-expect-error * Automated Type Error Fix
      const l2Categories = this.listing.businessNeeds[l1Category]?.categories ?? [];
      for (const l2Category of l2Categories) {
        // @ts-expect-error * Automated Type Error Fix
        accumulator.push(generateSimpleBadge(labels[l2Category] || l2Category));
      }
      return accumulator;
    }, [])];
    return badges;
  }
  get compatibilityWith() {
    const labels = this.i18n.listingDetails.productsSupported;
    const compatibilityFeatures = selectCompatibleWith(this.extensionListing);
    return compatibilityFeatures.map(key => generateSimpleBadge(labels[key] || key));
  }
  get requiresProducts() {
    const labels = this.i18n.listingDetails.productsSupported;
    const requiredProducts = selectRequiredProducts(this.extensionListing);
    return requiredProducts.map(key => generateSimpleBadge(labels[key] || key));
  }
  get prerequisiteAppsData() {
    const requiredListingIds = selectRequiredListings(this.extensionListing);
    if (!requiredListingIds || requiredListingIds.length === 0) {
      return {
        link: null,
        message: null
      };
    }
    const listingId = requiredListingIds[0];
    const listing = this.prerequisiteListing;
    if (!listing || !listing.name) {
      return {
        link: null,
        message: null
      };
    }
    if (listing.public === false) {
      return {
        link: null,
        message: this.i18n.listingDetails.partnerProducts.privateAppMessage
      };
    }
    return {
      link: {
        id: listingId,
        name: listing.name,
        url: `/${Routes.LISTING}?listingId=${listingId}`
      },
      message: null
    };
  }
  get hasPrerequisiteApps() {
    const requiredListingIds = selectRequiredListings(this.extensionListing);
    return requiredListingIds && requiredListingIds.length > 0;
  }

  // TODO - TECH_DEBT: This isn't a concern of the listing detail page, it shouldn't be here
  get reviewSummary() {
    return {
      // @ts-expect-error * Automated Type Error Fix
      listingId: this.listing?.oafId,
      // @ts-expect-error * Automated Type Error Fix
      reviewCount: this.listing?.reviewsSummary?.reviewCount,
      // @ts-expect-error * Automated Type Error Fix
      averageRating: this.listing?.reviewsSummary?.averageRating
    };
  }
  get isPrivateListing() {
    return !this.listing.public;
  }

  // @ts-expect-error * Automated Type Error Fix
  handleAnalytics(event) {
    event.detail.currentListingId = this.currentListingId;
  }
  get displayChat() {
    if (FeatureFlags.FF_DISABLE_AUTO_LOAD_QUALIFIED_CHAT) {
      return this.userToken && this.listing?.chat?.token && this.listing?.chat?.active && this.isQualifiedChatInitialized;
    }
    return this.userToken && this.listing?.chat?.token && this.listing?.chat?.active;
  }
  get showQualifiedChatButton() {
    return FeatureFlags.FF_DISABLE_AUTO_LOAD_QUALIFIED_CHAT && this.userToken && this.listing?.chat?.token && this.listing?.chat?.active;
  }
  get chatToken() {
    // @ts-expect-error * Automated Type Error Fix
    return this.listing?.chat?.token;
  }
  get searchBlurb() {
    return this.extensionListing?.searchBlurb || '';
  }
  get formattedDescription() {
    return parseNewLines(this.listing.description);
  }
  get isSupportedListing() {
    return this.listing && isSupportedTechnologyType(this.listing);
  }
  get carouselItems() {
    if (!this.listing) {
      return [];
    }
    // @ts-expect-error * Automated Type Error Fix
    const carouselItems = this.listing['listing/plugins/Carousel']?.items || [];
    // @ts-expect-error * Automated Type Error Fix
    const demoItems = this.listing['listing/plugins/Demo']?.items || [];
    const formattedCarouselItems = carouselItems.map(this.formatCarouselItem);
    const formattedDemoItems = demoItems.map(this.formatDemoItem);
    return [...formattedDemoItems, ...formattedCarouselItems];
  }
  get hasDemoLeadsEnabled() {
    return selectLeadTrialInformation(this.listing)?.enableDemoLeads ?? false;
  }
  get isFavoriteButtonDisabled() {
    return this.savedListingsLoadingStatus === SavedListingsLoadingStatus.LOADING;
  }
  get favoriteButtonClassName() {
    let className = 'favorite-button';
    // Adds 'disabled' class to avoid undesired hover behaviors on 'ax-iteractive-icon'
    if (this.isFavoriteButtonDisabled) {
      className += ' disabled';
    }
    return className;
  }
  get favoriteButtonIcon() {
    return this.listingInUserFavorites ? 'favorite-heart-filled' : 'favorite-heart';
  }
  get favoriteButtonIconDescription() {
    return this.listingInUserFavorites ? this.i18nModule.listingDetails.getItNowButtons.removeFavorite() : this.i18nModule.listingDetails.getItNowButtons.addFavorite();
  }
  // TODO - TECH_DEBT: The logic in these two `get` functions could be moved into a selector functions that could be reused
  get forcePackageListingExtensions() {
    return this.extensionListing?.extensions?.find(extension => extension.extensionType === EXTENSION_TYPE_PACKAGE_LISTING)?.data;
  }
  get forceAPIListingExtensions() {
    return this.extensionListing?.extensions?.find(extension => extension.extensionType === EXTENSION_TYPE_API_LISTING)?.data;
  }
  get forceTestDrive() {
    return forcePackageListingExtensions(this.listing)?.extensions?.find(extension => extension.extensionType === EXTENSION_TYPE_PACKAGE_TEST_DRIVE)?.data;
  }
  get forceAPITestDrive() {
    return forceAPIListingExtensions(this.listing)?.extensions?.find(extension => extension.extensionType === EXTENSION_TYPE_API_TEST_DRIVE)?.data;
  }
  handleTestDriveCTA() {
    this.redirectCallToActionToLegacySite('test');
  }
  get displayTestDriveCta() {
    return !!this.forceTestDrive?.testDriveOrgID || !!this.forceAPITestDrive?.testDriveOrgID;
  }
  /*LWC compiler v7.1.3*/
}
_registerDecorators(ListingDetail, {
  track: {
    prerequisiteListing: 1,
    tabs: 1
  },
  wire: {
    deviceType: {
      adapter: WireStore,
      dynamic: [],
      config: function ($cmp) {
        return {
          store: viewportStore,
          selector: selectDeviceType
        };
      }
    },
    routerStoreWire: {
      adapter: WireStore,
      dynamic: [],
      method: 1,
      config: function ($cmp) {
        return {
          store: routerStore,
          selector: selectParams
        };
      }
    },
    userStoreWire: {
      adapter: WireStore,
      dynamic: [],
      method: 1,
      config: function ($cmp) {
        return {
          store: userStore,
          selector: selectUserFavorite,
          equalityFn
        };
      }
    },
    i18nStoreWire: {
      adapter: WireStore,
      dynamic: [],
      method: 1,
      config: function ($cmp) {
        return {
          store: i18nStore,
          selector: selectI18n,
          equalityFn
        };
      }
    }
  },
  fields: ["i18n", "i18nModule", "userAuthenticated", "isLoading", "userToken", "userId", "savedListings", "savedListingsLoadingStatus", "isListingLoading", "listing", "publisher", "mobileMediaQuery", "currentListingId", "showOverview", "showReviews", "showMoreDetails", "tabsIds", "setTabsAriaControls", "focusActiveTab", "currentTab", "isQualifiedChatInitialized", "showQualifiedChatPanel", "isQualifiedChatLoading", "showTrialInformationAlert", "formatCarouselItem", "formatDemoItem"]
});
const __lwc_component_class_internal = _registerComponent(ListingDetail, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;